import { useCreateDialogLookModal } from 'components/create-dialog/CreateDialogModalFrame';
import { Checkbox } from 'components/ui/checkbox';
import HoursDisplay from 'components/util/HoursDisplay';
import {
    mapInvoiceTimecardFormToUpdateDto,
    mapInvoiceTimecardToInvoiceTimecardForm,
    MultiplierType,
} from 'pages/invoices/util/InvoicesPageUtil';
import LabelledData from 'pages/timecards/LabelledData';
import { timecardSecondsToHours } from 'pages/timecards/utils/timecardUtils';
import React, { useCallback, useEffect, useRef } from 'react';
import { Control, UseFormGetValues, UseFormHandleSubmit, UseFormReset, UseFormSetValue } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { InvoiceMatterForm, InvoiceTimecard, InvoiceTimecardForm } from 'types/invoice-details';
import { InvoiceData } from 'types/invoices';
import { NamedEntityMinDto } from 'types/other';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { captureTimecardUpdated } from 'utils/timecardAnalytics';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import {
    EmptySelection,
    getItemRowState,
    ITEM_CELL,
    ITEM_TOTAL_CELL,
    itemRowClassName,
    itemSelectionCellClassName,
    SelectionLabel,
} from '../InvoiceItemRow';
import InvoiceTimecardEditModal from './InvoiceTimecardEditModal';
import { AdvancedBillingDisplay } from './component/AdvancedBillingDisplay';
import { BilledHoursDisplay } from './component/BilledHoursDisplay';
import { DescriptionDisplay } from './component/DescriptionDisplay';
import { EmployeeDisplay } from './component/EmployeeDisplay';
import { TakeActionBackDisplay } from './component/TakeActionBackDisplay';
import { TotalAmountDisplay } from './component/TotalAmountDisplay';

const TimecardSelectionCheckbox = ({
    id,
    checked,
    disabled,
    onChange,
}: {
    id: string;
    checked: boolean;
    disabled: boolean;
    onChange: () => void;
}) => {
    return (
        <SelectionLabel htmlFor={id}>
            <span className="flex min-h-8 items-center">
                <Checkbox
                    id={id}
                    variant="secondary"
                    disabled={disabled}
                    checked={checked}
                    onCheckedChange={onChange}
                />
            </span>
        </SelectionLabel>
    );
};

export interface InvoiceTimecardRowProps {
    id: number;
    invoiceTimecard: InvoiceTimecard;
    timecardForm: InvoiceTimecardForm | undefined;
    isNew: boolean;
    readonly: boolean;
    index: number;
    matterAssingmentList: NamedEntityMinDto[];
    invoiceId: number;
    invoice: InvoiceData;
    control: Control<InvoiceMatterForm>;
    validateForm: UseFormHandleSubmit<InvoiceMatterForm>;
    setValue: UseFormSetValue<InvoiceMatterForm>;
    getValues: UseFormGetValues<InvoiceMatterForm>;
    reset: UseFormReset<InvoiceMatterForm>;
    handleUpdateInvoiceTimecards: (idList: number[], updatedTimecards: InvoiceTimecardForm[]) => void;
    classifiers: Record<string, ClassifierData>;
    advancedBillingEnabled: boolean;
    shouldOpenModal?: boolean;
    clearModalRequest?: () => void;
    updateInvoiceTimecard: ReturnType<typeof import('store/api/invoice-details').useUpdateInvoiceTimecardMutation>[0];
    nonBillableBgColor: string | undefined;
    isCredit: boolean;
    isCorrectionInvoice: boolean;
    isSent: boolean;
    isPaid: boolean;
    multiplierTypes: MultiplierType[];
    buttonsDisabled: boolean;
    isTwoDecimalDisplayEnabled: boolean;
    isTwoDecimalEntryEnabled: boolean;
    displayDecimalScale: 1 | 2;
    isSelected: boolean;
    toggleSelection: (id: number) => void;
}

/** A timecard row's state, its saves and its edit dialog, as both designs' rows draw them. */
export const useInvoiceTimecardRow = (props: InvoiceTimecardRowProps) => {
    const {
        invoiceTimecard,
        timecardForm,
        isNew,
        index,
        readonly,
        invoiceId,
        invoice,
        matterAssingmentList,
        setValue,
        getValues,
        classifiers,
        shouldOpenModal,
        clearModalRequest,
        updateInvoiceTimecard,
        nonBillableBgColor,
        isCorrectionInvoice,
        isSent,
        isPaid,
        multiplierTypes,
        isTwoDecimalEntryEnabled,
        isSelected,
    } = props;

    const { isRemoved: isRemovedViaCorrection } = invoiceTimecard;
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);
    const { openModal, closeModal } = useCreateDialogLookModal();

    const invoiceTimecardEmployee = timecardForm?.employee ?? null;
    const invoiceTimecardDate = timecardForm?.timecardDate ?? null;
    const invoiceTimecardHourlyPrice = timecardForm?.hourlyPrice;
    const invoiceTimecardActivityTypeCode = timecardForm?.timecardActivityType?.value ?? null;
    const selected = isSelected;
    const editIntentTypeCode = timecardForm?.editIntentTypeCode ?? null;
    const isNonBillable = timecardForm?.isNonBillable;

    const isRowDisabled = editIntentTypeCode !== null || isRemovedViaCorrection;
    const isRemoveActionDisabled = isCorrectionInvoice && (isSent || isPaid);
    const canTakeActionBack = !readonly && !isRemovedViaCorrection;
    const hideSelectionCheckbox = readonly || isRowDisabled || isRemoveActionDisabled;

    const rowState = getItemRowState({
        isSelected: selected,
        isNew,
        isRowDisabled,
        isCorrectionInvoice,
        readonly,
    });

    // The tenant's colour for non-billable rows: data, so it stays an inline style.
    const nonBillableBgStyle =
        isNonBillable && nonBillableBgColor && !rowState ? { backgroundColor: `#${nonBillableBgColor}` } : undefined;

    const isModalSubmittingRef = useRef(false);

    const guardedCloseModal = useCallback(() => {
        if (!isModalSubmittingRef.current) closeModal();
    }, [closeModal]);

    const onSubmit = useCallback(async (): Promise<void> => {
        const formData: InvoiceTimecardForm = getValues(`invoiceTimecards.${index}`);
        const resp = await updateInvoiceTimecard(mapInvoiceTimecardFormToUpdateDto(formData, invoiceId)).unwrap();
        const updated = mapInvoiceTimecardToInvoiceTimecardForm(
            resp,
            classifiers,
            isTwoDecimalEntryEnabled,
            isPaymentCurrencyMode
        );
        setValue(`invoiceTimecards.${index}`, updated);
    }, [
        classifiers,
        getValues,
        index,
        invoiceId,
        setValue,
        updateInvoiceTimecard,
        isTwoDecimalEntryEnabled,
        isPaymentCurrencyMode,
    ]);

    const submitWithCatch = useCallback(() => {
        void onSubmit().catch(console.error);
    }, [onSubmit]);

    // Of the row's inline saves, only the description's reports an update: hours and billing edits send none.
    const submitDescription = useCallback(() => {
        const description = getValues(`invoiceTimecards.${index}.description`);
        void onSubmit()
            .then(() =>
                captureTimecardUpdated({
                    via: 'invoice',
                    descriptionChanged: description !== invoiceTimecard.description,
                    description,
                    timecardLanguageCode: invoiceTimecard.matter.timecardLanguageCode,
                })
            )
            .catch(console.error);
    }, [getValues, index, invoiceTimecard, onSubmit]);

    const onSave = useCallback(
        async (formData: InvoiceTimecardForm): Promise<void> => {
            const resp = await updateInvoiceTimecard({
                ...mapInvoiceTimecardFormToUpdateDto(formData, invoiceId),
                skipButtonDisable: true,
            }).unwrap();
            const updated = mapInvoiceTimecardToInvoiceTimecardForm(
                resp,
                classifiers,
                isTwoDecimalEntryEnabled,
                isPaymentCurrencyMode
            );
            setValue(`invoiceTimecards.${index}`, updated);
            successAlert(t('timecards.timecard-updated'));
            // Checked against the case the save puts the timecard on, which the modal may have changed.
            void captureTimecardUpdated({
                via: 'invoice',
                descriptionChanged: formData.description !== invoiceTimecard.description,
                description: formData.description,
                timecardLanguageCode: formData.matter?.timecardLanguageCode,
            });
        },
        [
            classifiers,
            index,
            invoiceId,
            invoiceTimecard,
            setValue,
            updateInvoiceTimecard,
            isTwoDecimalEntryEnabled,
            isPaymentCurrencyMode,
        ]
    );

    const openInvoiceTimecardEditModal = useCallback((): void => {
        openModal(
            <InvoiceTimecardEditModal
                invoice={invoice}
                onSave={onSave}
                onClose={guardedCloseModal}
                initialData={getValues(`invoiceTimecards.${index}`)}
                classifiers={classifiers}
                multiplierTypes={multiplierTypes}
                matterAssingmentList={matterAssingmentList}
                invoiceTimecard={invoiceTimecard}
                isSubmittingRef={isModalSubmittingRef}
            />,
            guardedCloseModal,
            {
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto',
                width: '700px',
            }
        );
    }, [
        invoice,
        openModal,
        onSave,
        guardedCloseModal,
        getValues,
        index,
        classifiers,
        multiplierTypes,
        matterAssingmentList,
        invoiceTimecard,
    ]);

    // Auto-open modal when requested after duplication
    useEffect(() => {
        if (shouldOpenModal && clearModalRequest && index >= 0) {
            openInvoiceTimecardEditModal();
            clearModalRequest();
        }
    }, [index, shouldOpenModal, clearModalRequest, openInvoiceTimecardEditModal]);

    return {
        isPaymentCurrencyMode,
        displayCurrency,
        invoiceTimecardEmployee,
        invoiceTimecardDate,
        invoiceTimecardHourlyPrice,
        invoiceTimecardActivityTypeCode,
        editIntentTypeCode,
        isNonBillable,
        isRowDisabled,
        canTakeActionBack,
        hideSelectionCheckbox,
        rowState,
        nonBillableBgStyle,
        submitWithCatch,
        submitDescription,
        openInvoiceTimecardEditModal,
    };
};

type Props = InvoiceTimecardRowProps;

const InvoiceTimecardItem = React.memo<Props>(
    (props) => {
        const {
            invoiceTimecard,
            timecardForm,
            index,
            readonly,
            control,
            setValue,
            getValues,
            handleUpdateInvoiceTimecards,
            classifiers,
            advancedBillingEnabled,
            isCredit,
            multiplierTypes,
            buttonsDisabled,
            isTwoDecimalDisplayEnabled,
            isTwoDecimalEntryEnabled,
            displayDecimalScale,
            isSelected,
            toggleSelection,
        } = props;
        const {
            isPaymentCurrencyMode,
            displayCurrency,
            invoiceTimecardEmployee,
            invoiceTimecardDate,
            invoiceTimecardHourlyPrice,
            invoiceTimecardActivityTypeCode,
            editIntentTypeCode,
            isNonBillable,
            isRowDisabled,
            canTakeActionBack,
            hideSelectionCheckbox,
            rowState,
            nonBillableBgStyle,
            submitWithCatch,
            submitDescription,
            openInvoiceTimecardEditModal,
        } = useInvoiceTimecardRow(props);

        return (
            <div
                className={itemRowClassName(advancedBillingEnabled ? 'timecardAdvanced' : 'basic', rowState)}
                style={nonBillableBgStyle}
            >
                <div className={itemSelectionCellClassName(readonly)}>
                    {hideSelectionCheckbox ? (
                        <EmptySelection />
                    ) : (
                        <TimecardSelectionCheckbox
                            id={`timecard-select-${invoiceTimecard.id}`}
                            checked={isSelected}
                            disabled={buttonsDisabled}
                            onChange={() => toggleSelection(invoiceTimecard.id)}
                        />
                    )}
                </div>
                <div className={ITEM_CELL}>
                    {isRowDisabled && canTakeActionBack ? (
                        <TakeActionBackDisplay
                            index={index}
                            invoiceTimecard={invoiceTimecard}
                            editIntentTypeCode={editIntentTypeCode}
                            setValue={setValue}
                            getValues={getValues}
                            control={control}
                            handleUpdateInvoiceTimecards={handleUpdateInvoiceTimecards}
                        />
                    ) : null}
                    <EmployeeDisplay
                        employee={invoiceTimecardEmployee}
                        timecardDate={invoiceTimecardDate}
                        classifiers={classifiers}
                        invoiceTimecard={invoiceTimecard}
                        timecardActivityTypeCode={invoiceTimecardActivityTypeCode}
                        isNonBillable={isNonBillable}
                        hideEditButton={isCredit || readonly || isRowDisabled}
                        handleOnClickEditButton={openInvoiceTimecardEditModal}
                        buttonsDisabled={buttonsDisabled}
                    />
                    <DescriptionDisplay
                        invoiceTimecard={invoiceTimecard}
                        readonly={readonly}
                        isRowDisabled={isRowDisabled}
                        control={control}
                        index={index}
                        onSubmit={submitDescription}
                        buttonsDisabled={buttonsDisabled}
                    />
                </div>
                <div className={ITEM_CELL}>
                    <div className="opacity-decreasing-when-disabled flex flex-col">
                        <BilledHoursDisplay
                            invoiceTimecard={invoiceTimecard}
                            readonly={readonly}
                            isRowDisabled={isRowDisabled}
                            advancedBillingEnabled={advancedBillingEnabled}
                            control={control}
                            index={index}
                            onSubmit={submitWithCatch}
                            setValue={setValue}
                            buttonsDisabled={buttonsDisabled}
                            isTwoDecimalDisplayEnabled={isTwoDecimalDisplayEnabled}
                            isTwoDecimalEntryEnabled={isTwoDecimalEntryEnabled}
                            displayDecimalScale={displayDecimalScale}
                        />
                        <LabelledData label="invoices.details.table.time-used" inline>
                            <span className="font-semibold">
                                <HoursDisplay
                                    value={timecardSecondsToHours(
                                        timecardForm?.totalTimeInSeconds ?? 0,
                                        isTwoDecimalDisplayEnabled
                                    )}
                                />
                            </span>
                        </LabelledData>
                        <LabelledData label="invoices.details.table.hourly-rate-used" inline>
                            <span className="font-semibold whitespace-nowrap">
                                {invoiceTimecardHourlyPrice == null
                                    ? '-'
                                    : `${invoiceTimecardHourlyPrice} ${displayCurrency}/h`}
                            </span>
                        </LabelledData>
                    </div>
                </div>
                {advancedBillingEnabled ? (
                    <div className={ITEM_CELL}>
                        <AdvancedBillingDisplay
                            readonly={readonly}
                            isRowDisabled={isRowDisabled}
                            control={control}
                            index={index}
                            onSubmit={submitWithCatch}
                            setValue={setValue}
                            getValues={getValues}
                            multiplierTypes={multiplierTypes}
                            invoiceTimecard={invoiceTimecard}
                            handleUpdateInvoiceTimecards={handleUpdateInvoiceTimecards}
                            buttonsDisabled={buttonsDisabled}
                            isPaymentCurrencyMode={isPaymentCurrencyMode}
                        />
                    </div>
                ) : null}
                <div className={ITEM_TOTAL_CELL}>
                    <TotalAmountDisplay
                        invoiceTimecard={invoiceTimecard}
                        currency={displayCurrency}
                        decimalScale={displayDecimalScale}
                        isPaymentCurrencyMode={isPaymentCurrencyMode}
                    />
                </div>
            </div>
        );
    },
    (prev, next) => {
        const pf = prev.timecardForm;
        const nf = next.timecardForm;
        return (
            prev.index === next.index &&
            prev.readonly === next.readonly &&
            prev.isNew === next.isNew &&
            prev.advancedBillingEnabled === next.advancedBillingEnabled &&
            prev.shouldOpenModal === next.shouldOpenModal &&
            prev.buttonsDisabled === next.buttonsDisabled &&
            prev.isSelected === next.isSelected &&
            prev.nonBillableBgColor === next.nonBillableBgColor &&
            prev.isTwoDecimalDisplayEnabled === next.isTwoDecimalDisplayEnabled &&
            prev.isTwoDecimalEntryEnabled === next.isTwoDecimalEntryEnabled &&
            prev.displayDecimalScale === next.displayDecimalScale &&
            pf?.employee?.id === nf?.employee?.id &&
            pf?.timecardDate === nf?.timecardDate &&
            pf?.hourlyPrice === nf?.hourlyPrice &&
            pf?.totalTimeInHours === nf?.totalTimeInHours &&
            pf?.timecardActivityType?.value === nf?.timecardActivityType?.value &&
            pf?.editIntentTypeCode === nf?.editIntentTypeCode &&
            pf?.isNonBillable === nf?.isNonBillable &&
            pf?.newMatter?.id === nf?.newMatter?.id &&
            prev.invoiceTimecard.isRemoved === next.invoiceTimecard.isRemoved &&
            (prev.invoiceTimecard.issues ?? []).join() === (next.invoiceTimecard.issues ?? []).join() &&
            prev.invoiceTimecard.isNonBillable === next.invoiceTimecard.isNonBillable &&
            prev.invoiceTimecard.description === next.invoiceTimecard.description &&
            prev.invoiceTimecard.notes === next.invoiceTimecard.notes &&
            prev.invoiceTimecard.hourlyPrice === next.invoiceTimecard.hourlyPrice &&
            prev.invoiceTimecard.hourlyPriceInPaymentCurrency === next.invoiceTimecard.hourlyPriceInPaymentCurrency &&
            prev.invoiceTimecard.billedTimeInSeconds === next.invoiceTimecard.billedTimeInSeconds &&
            prev.invoiceTimecard.calculationLog?.initialAmount === next.invoiceTimecard.calculationLog?.initialAmount &&
            prev.invoiceTimecard.calculationLog?.finalAmount === next.invoiceTimecard.calculationLog?.finalAmount &&
            prev.invoiceTimecard.calculationLog?.paymentCurrency?.initialAmount ===
                next.invoiceTimecard.calculationLog?.paymentCurrency?.initialAmount &&
            prev.invoiceTimecard.calculationLog?.paymentCurrency?.finalAmount ===
                next.invoiceTimecard.calculationLog?.paymentCurrency?.finalAmount
        );
    }
);

export default InvoiceTimecardItem;
